/* eslint-disable camelcase,import/no-cycle */
import Vue from 'vue';
import _ from 'lodash';
import { productService } from '@/_services';
import { generic_storage } from '../_utilities/generic_storage';

const extendedCatalogStore = generic_storage('productService');

extendedCatalogStore.state = {
  ...extendedCatalogStore.state,
  search: '',
  filters: [],
  facetTree: {}, // empty initial tree
  facetTreeSet: false,
  itemLoading: false,
  facetLoading: true,
  facets: [
    /*
       Holds array of objects with this structure:  (via FacetSerializer in the Django)
        {
          name:
          id:
          order:
          facet_values: [(dict from FacetValueSerializer)]
        }
    */
  ],
  selectedFacets: [], // array of FacetValue id ints
  selectedFacetsName: [],
  filterDrawerIsOpen: false,
  sortingValues: [],
  abortControllers: null,
};

extendedCatalogStore.getters = {
  ...extendedCatalogStore.getters,
  selectedProducts: (state) => {
    // eslint-disable-next-line no-var
    var results = [];
    // eslint-disable-next-line array-callback-return
    state.items.filter((item) => item.selected).map((item) => {
      // eslint-disable-next-line array-callback-return
      item.images.filter((image) => image.selected).map((image) => {
        results.push({
          pk: image.pk,
          image_url: image.image_url,
          site_url: item.site_url,
          product: item,
          brand: item.brand.name,
          description: item.title,
          // Required to change from Catalog to Product fetching in Product Selector
          meta: item.meta ?? `{ "site_url": "${item.site_url}", "external_id": -1 }`,
        });
      });
    });
    return results;
  },
  facetByID: (state) => (id) => {
    const flatFacetsValues = state.facets
      .map((facet) => (facet.facet_values.length > 0 ? facet.facet_values : null))
      .filter((facet) => facet)
      .flat();
    return flatFacetsValues.find((facet) => facet.id === id);
  },

  selectedFacets: (state) => state.selectedFacets,
  selectedFacetsName: (state) => state.selectedFacetsName,
  facets: (state) => state.facets,
  facetLoading: (state) => state.facetLoading,

  filterDrawerIsOpen: (state) => state.filterDrawerIsOpen,
  sortingItems: (state) => state.sortingValues.map((sv) => sv.value),
  selectedSort: (state) => {
    const index = state.sortingValues.findIndex((sv) => sv.selected);
    return index > -1 ? index : 0;
  },
};

extendedCatalogStore.actions = {
  ...extendedCatalogStore.actions,
  fetchProduct({ commit }, id) {
    commit('setItemLoading', true);
    productService.getItem(id).then((r) => {
      if (r.data) {
        commit('setItem', r.data);
      }
    }).catch((e) => {
      console.log(e);
    }).finally(() => {
      commit('setItemLoading', false);
    });
  },
  fetchFacets({ commit, state }, { specialHandling, additionalParams }) {
    if (state.abortController) {
      state.abortController.abort();
      commit('deleteAbortController');
    }
    commit('setFacetLoading', true);
    const params = {};
    if (specialHandling) {
      params[specialHandling] = true;
    }
    if (additionalParams) {
      // eslint-disable-next-line
      for (const [k, v] of Object.entries(additionalParams)) {
        params[k] = v;
      }
    }
    const controller = new AbortController();
    commit('saveAbortController', controller);
    productService.getFacets(params, state.abortController).then((r) => {
      commit('deleteAbortController');
      commit('setFacets', r.data.results.map((facet) => ({
        ...facet,
        facet_values: facet.facet_values.map((fv) => {
          Object.defineProperty(fv, 'name', Object.getOwnPropertyDescriptor(fv, 'value'));
          delete fv.value;
          return fv;
        }),
      })));
      if (!state.facetTreeSet) {
        commit('populateFacets', '');
        state.facetTreeSet = true;
      }
    }).catch((e) => {
      console.log(e);
    }).finally(() => {
      commit('setFacetLoading', false);
    });
  },
  fetchSortingValues({ commit }) {
    return productService.getSortingValues().then((r) => {
      commit('setSortingValues', _.sortBy(r.data.results, ['order']));
      return r;
    });
  },
  selectSortingValue({ commit, getters }, index) {
    commit('setSortingValueSelectStatus', { index: getters.selectedSort, value: false });
    commit('setSortingValueSelectStatus', { index, value: true });
  },
  clear({ dispatch }) {
    dispatch('clearSelectedProducts');
    dispatch('clearFilters');
  },
  clearSelectedProducts({ state }) {
    state.items.filter((item) => item.selected).map((item) => {
      item.selected = false;
      return item;
    });
  },
  clearFilters({ commit }) {
    commit('clearFacets');
  },
  toggleSelectedFilter({ state, commit, dispatch }, filter) {
    const index = state.selectedFacets.indexOf(filter.id);
    if (index > -1) {
      dispatch('removeSelectedFilterAndChildren', filter);
    } else {
      commit('addSelectedFacet', filter);
    }
  },
  removeSelectedFilterAndChildren({ state, commit, dispatch }, facet) {
    // Remove the facet first
    const tempFacet = state.selectedFacetsName.find((obj) => obj.id === facet.id);
    if (!tempFacet) return;
    const facetId = tempFacet.facet_id || null;
    // Then recursively remove all its child facets
    const childFacets = [];
    if (state.facetTree[facetId]) {
      state.facetTree[facetId].forEach((childFacetId) => {
        const tempFacet2 = state.selectedFacetsName.filter((obj) => obj.facet_id === childFacetId);
        if (tempFacet2.length) {
          childFacets.push(...tempFacet2);
        }
      });
      for (let i = 0; i < childFacets.length; i++) {
        dispatch('removeSelectedFilterAndChildren', childFacets[i]);
      }
    }
    commit('removeSelectedFacet', facet.id);
  },
  unselectFilter({ state, commit }, filter) {
    const index = [state.facets.map((f) => f.name).indexOf(filter.facet_name)];
    index.push(state.facets[index[0]].facet_values.map((fv) => fv.name).indexOf(filter.name));
    commit('unselectFacet', index);
  },
  toggleFilterDrawer({ state, commit }) {
    commit('setFilterDrawerIsOpen', !state.filterDrawerIsOpen);
  },
  customReset({ commit }) {
    commit('customReset');
  },
};
extendedCatalogStore.mutations = {
  ...extendedCatalogStore.mutations,
  setItems(state, items) {
    state.items = items;
  },
  setItem(state, item) {
    state.item = item;
  },
  setItemLoading(state, loading) {
    state.itemLoading = loading;
  },
  setFacetLoading(state, loading) {
    state.facetLoading = loading;
  },
  setSearch(state, search) {
    state.search = search;
  },
  setFacets(state, facets) {
    Vue.set(state, 'facets', facets);
  },
  setSortingValues(state, items) {
    state.sortingValues = items;
  },
  setSortingValueSelectStatus(state, { index, value }) {
    Vue.set(state.sortingValues, index, { ...state.sortingValues[index], selected: value });
  },
  setFilterDrawerIsOpen(state, value) {
    state.filterDrawerIsOpen = value;
  },
  addSelectedFacet(state, facetValue) {
    state.selectedFacets.push(facetValue.id);
    const removeLast = facetValue.facet.substring(0, facetValue.facet.length - 1);
    const lastSlashIndex = removeLast.lastIndexOf('/');
    const ID = parseInt(removeLast.substring(lastSlashIndex + 1), 10);
    facetValue.facet_id = ID;
    state.selectedFacetsName.push(facetValue);
  },
  populateFacets(state, __) {
    if (state.facetTreeSet) return;
    const facetsData = state.facets;

    // Populate facetTree
    for (let i = 0; i < facetsData.length; i++) {
      const { id, parent } = facetsData[i];
      if (!state.facetTree[parent]) {
        state.facetTree[parent] = [];
      }
      state.facetTree[parent].push(id);
    }
  },
  removeSelectedFacet(state, facetId) {
    state.selectedFacets = state.selectedFacets.filter((facet) => facet !== facetId);
    state.selectedFacetsName = state.selectedFacetsName.filter((facet) => facet.id !== facetId);
  },
  clearFacets(state) {
    state.selectedFacets = [];
    state.selectedFacetsName = [];
  },
  customReset(state) {
    // Duplicate of generic reset because that one wasn't working, probably due to a vuex plugin
    state.isLoading = false;
    state.item = {};
    state.items = [];
    state.root = {};
  },
  saveAbortController(state, controller) {
    state.abortController = controller;
  },
  deleteAbortController(state) {
    state.abortController = null;
  },
};

export const catalog = extendedCatalogStore;
